import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function BinomialOptionsCalculator() {
  const { t } = useTranslation();
  const [spotPrice, setSpotPrice] = useState("");
  const [strikePrice, setStrikePrice] = useState("");
  const [timeToExpiry, setTimeToExpiry] = useState("");
  const [volatility, setVolatility] = useState("");
  const [riskFreeRate, setRiskFreeRate] = useState("");
  const [steps, setSteps] = useState("10");
  const [optionType, setOptionType] = useState("call");
  const [exerciseType, setExerciseType] = useState("american");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: 'https://calclab.app' },
      { name: t('categories.finance'), url: 'https://calclab.app/finance' },
      { name: "Binomial Options Calculator", url: "https://calclab.app/calculator/binomial-options" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Binomial Options Pricing Calculator",
      "Price American and European options using binomial tree model. Calculate option values with customizable steps and parameters.",
      "https://calclab.app/calculator/binomial-options",
      lang
    )
  ]);

  const calculate = () => {
    const S = parseFloat(spotPrice);
    const K = parseFloat(strikePrice);
    const T = parseFloat(timeToExpiry) / 365;
    const sigma = parseFloat(volatility) / 100;
    const r = parseFloat(riskFreeRate) / 100;
    const n = parseInt(steps);

    if (isNaN(S) || isNaN(K) || isNaN(T) || isNaN(sigma) || isNaN(r) || isNaN(n) ||
        S <= 0 || K <= 0 || T <= 0 || sigma <= 0 || n <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    const dt = T / n;
    const u = Math.exp(sigma * Math.sqrt(dt));
    const d = 1 / u;
    const p = (Math.exp(r * dt) - d) / (u - d);

    // Build price tree
    const prices: number[][] = [];
    for (let i = 0; i <= n; i++) {
      prices[i] = [];
      for (let j = 0; j <= i; j++) {
        prices[i][j] = S * Math.pow(u, j) * Math.pow(d, i - j);
      }
    }

    // Calculate option values at maturity
    const values: number[][] = [];
    for (let i = 0; i <= n; i++) {
      values[i] = [];
    }

    for (let j = 0; j <= n; j++) {
      if (optionType === "call") {
        values[n][j] = Math.max(0, prices[n][j] - K);
      } else {
        values[n][j] = Math.max(0, K - prices[n][j]);
      }
    }

    // Backward induction
    for (let i = n - 1; i >= 0; i--) {
      for (let j = 0; j <= i; j++) {
        const holdValue = Math.exp(-r * dt) * (p * values[i + 1][j + 1] + (1 - p) * values[i + 1][j]);
        
        if (exerciseType === "american") {
          const exerciseValue = optionType === "call" 
            ? Math.max(0, prices[i][j] - K)
            : Math.max(0, K - prices[i][j]);
          values[i][j] = Math.max(holdValue, exerciseValue);
        } else {
          values[i][j] = holdValue;
        }
      }
    }

    setResult(values[0][0]);
    toast.success("Option price calculated!");
  };

  const reset = () => {
    setSpotPrice("");
    setStrikePrice("");
    setTimeToExpiry("");
    setVolatility("");
    setRiskFreeRate("");
    setSteps("10");
    setOptionType("call");
    setExerciseType("american");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Binomial Options Pricing Calculator</CardTitle>
          <CardDescription>
            Price American and European options using binomial tree model
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Option Type</Label>
              <Select value={optionType} onValueChange={setOptionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="put">Put</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="exercise">Exercise Type</Label>
              <Select value={exerciseType} onValueChange={setExerciseType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="american">American</SelectItem>
                  <SelectItem value="european">European</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="spot">Spot Price ($)</Label>
            <Input
              id="spot"
              type="number"
              placeholder="100"
              value={spotPrice}
              onChange={(e) => setSpotPrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="strike">Strike Price ($)</Label>
            <Input
              id="strike"
              type="number"
              placeholder="105"
              value={strikePrice}
              onChange={(e) => setStrikePrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="time">Time to Expiry (days)</Label>
            <Input
              id="time"
              type="number"
              placeholder="30"
              value={timeToExpiry}
              onChange={(e) => setTimeToExpiry(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vol">Volatility (%)</Label>
            <Input
              id="vol"
              type="number"
              placeholder="25"
              value={volatility}
              onChange={(e) => setVolatility(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate">Risk-Free Rate (%)</Label>
            <Input
              id="rate"
              type="number"
              placeholder="5"
              value={riskFreeRate}
              onChange={(e) => setRiskFreeRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="steps">Number of Steps</Label>
            <Input
              id="steps"
              type="number"
              placeholder="10"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Option Price:</strong> ${result.toFixed(4)}
                </p>
                <CopyResultButton value={result.toFixed(4)} />
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["binomial-options"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
